import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { Batch, BatchSummary } from '../../core/services/batch';
import { IngestionRealtime } from '../../core/services/ingestion-realtime';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { Button } from '../../shared/components/button/button';
import { CreateBatchModal } from '../../shared/components/create-batch-modal/create-batch-modal';
import { BatchCreatedModal } from '../../shared/components/batch-created-modal/batch-created-modal';
import { idsEqual } from '../../shared/constants/batch-status';
import { VersionControl } from '../../core/services/version-control';

@Component({
  selector: 'app-manage-batches',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar, ToastComponent, Button, CreateBatchModal, BatchCreatedModal],
  templateUrl: './manage-batches.html',
  styleUrl: './manage-batches.css'
})
export class ManageBatches implements OnInit, OnDestroy {
  private batchService = inject(Batch);
  private toast = inject(Toast);
  router = inject(Router);
  private realtime = inject(IngestionRealtime);
  private versionControl = inject(VersionControl);
  /** Version Review cards waiting, in total and per batch (lower-cased batch id). */
  pendingReviews = signal(0);
  private pendingByBatch = signal<Record<string, number>>({});
  private subscriptions = new Subscription();
  private joinedIds = new Set<string>();

  batches = signal<BatchSummary[]>([]);

  allCount = signal(0);
  completedCount = signal(0);
  processingCount = signal(0);
  preparingCount = signal(0);
  failedCount = signal(0);
  draftCount = signal(0);

  activeFilter = signal<string | null>(null);
  isLoading = signal(true);

  searchQuery = signal('');
  currentPage = signal(1);
  pageSize = 9;
  totalItems = signal(0);

  selectedIds = signal<Set<string>>(new Set());
  isIngesting = signal(false);

  showCreateBatchModal = signal(false);
  showBatchCreatedModal = signal(false);
  isCreatingBatch = signal(false);

  ngOnInit() {
    this.registerRealtimeHandlers();
    this.loadBatches();
    this.loadCounts();
    this.loadPendingReviews();
  }

  private loadPendingReviews() {
    this.versionControl.pendingByBatch().subscribe({
      next: res => {
        this.pendingReviews.set(res.total);
        this.pendingByBatch.set(Object.fromEntries(res.batches.map(b => [b.batchId.toLowerCase(), b.pending])));
      },
      // The indicator is a hint; a failed lookup just leaves it hidden.
      error: () => this.pendingReviews.set(0)
    });
  }

  pendingFor(batchId: string): number {
    return this.pendingByBatch()[batchId.toLowerCase()] ?? 0;
  }

  openVersionReview(event?: Event) {
    event?.stopPropagation();
    this.router.navigate(['/version-review']);
  }

  ngOnDestroy(): void {
    this.joinedIds.forEach(id => this.realtime.leaveBatch(id));
    this.subscriptions.unsubscribe();
  }

  private loadCounts() {
    this.batchService.getBatches(undefined, 1, 1).subscribe({
      next: (res) => this.allCount.set(res.data.total)
    });
    this.batchService.getBatches('Completed', 1, 1).subscribe({
      next: (res) => this.completedCount.set(res.data.total)
    });
    this.batchService.getBatches('Processing', 1, 1).subscribe({
      next: (res) => this.processingCount.set(res.data.total)
    });
    this.batchService.getBatches('Enumerating', 1, 1).subscribe({
      next: (res) => this.preparingCount.set(res.data.total)
    });
    this.batchService.getBatches('Failed', 1, 1).subscribe({
      next: (res) => this.failedCount.set(res.data.total)
    });
    this.batchService.getBatches(this.toApiStatus('Draft'), 1, 1).subscribe({
      next: (res) => this.draftCount.set(res.data.total)
    });
  }

  // The API's BatchStatus enum has no "Draft" member — un-ingested batches are
  // stored as "Created" and only labeled "Draft" in API responses for display.
  // The status query param is matched against the raw enum name, so translate here.
  private toApiStatus(status: string | null): string | undefined {
    if (!status) return undefined;
    if (status === 'Draft') return 'Created';
    if (status === 'Preparing') return 'Enumerating';
    return status;
  }

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
    this.loadBatches(this.activeFilter(), 1);
  }

  loadBatches(status: string | null = null, page: number = 1) {
    this.isLoading.set(true);
    this.activeFilter.set(status);
    this.currentPage.set(page);

    this.batchService.getBatches(this.toApiStatus(status), page, this.pageSize).subscribe({
      next: (res) => {
        this.isLoading.set(false);
        const search = this.searchQuery().trim().toLowerCase();
        const items = search
          ? res.data.items.filter(b => b.name.toLowerCase().includes(search))
          : res.data.items;

        this.batches.set(items);
        this.totalItems.set(search ? items.length : res.data.total);
        this.syncBatchGroups();
      },
      error: (err) => {
        this.isLoading.set(false);
        const message = err?.error?.message || 'Failed to load batches.';
        this.toast.show(message, 'error');
      }
    });
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems() / this.pageSize) || 1;
  }

  nextPage() {
    if (this.currentPage() < this.totalPages) {
      this.loadBatches(this.activeFilter(), this.currentPage() + 1);
    }
  }

  prevPage() {
    if (this.currentPage() > 1) {
      this.loadBatches(this.activeFilter(), this.currentPage() - 1);
    }
  }

  statusClass(status: string): string {
    switch (status) {
      case 'Completed': return 'badge-completed';
      case 'Processing': return 'badge-processing';
      case 'Preparing': return 'badge-preparing';
      case 'Failed': return 'badge-failed';
      default: return 'badge-default';
    }
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  toggleSelect(id: string, event: Event) {
    event.stopPropagation();
    const current = new Set(this.selectedIds());
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    this.selectedIds.set(current);
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  get hasSelection(): boolean {
    return this.selectedIds().size > 0;
  }

  openCreateBatchModal() {
    this.showCreateBatchModal.set(true);
  }

  closeCreateBatchModal() {
    this.showCreateBatchModal.set(false);
  }

  onSaveBatch(data: { name: string; description: string }) {
    this.isCreatingBatch.set(true);

    this.batchService.createBatch({ name: data.name, description: data.description }).subscribe({
      next: (res) => {
        this.isCreatingBatch.set(false);
        this.showCreateBatchModal.set(false);
        this.showBatchCreatedModal.set(true);

        // Status starts as "Preparing" (backend: Enumerating) — the SharePoint folder walk
        // runs in the background now, so this row isn't really "Draft" yet. The
        // BatchCreationCompleted/BatchCreationFailed handlers below flip it once the
        // background worker finishes, whether or not this page is still open.
        const newBatch: BatchSummary = {
          id: res.data.batchId,
          name: res.data.name,
          description: res.data.description,
          sourceType: 'SharePoint',
          driveId: null,
          folderId: null,
          status: this.normalizeBatchStatus(res.data.status),
          totalDocuments: 0,
          processedDocuments: 0,
          failedDocuments: 0,
          percentComplete: 0,
          createdAt: new Date().toISOString(),
          completedAt: null
        };

        this.batches.update(items => [newBatch, ...items]);
        this.allCount.update(c => c + 1);
        this.totalItems.update(c => c + 1);
        this.joinedIds.add(newBatch.id.toLowerCase());
        void this.realtime.joinBatch(newBatch.id);
      },
      error: (err) => {
        this.isCreatingBatch.set(false);
        const message = err?.error?.message || 'Failed to create batch. Please try again.';
        this.toast.show(message, 'error');
      }
    });
  }

  closeBatchCreatedModal() {
    this.showBatchCreatedModal.set(false);
  }

  onCreateMoreBatch() {
    this.showBatchCreatedModal.set(false);
    this.showCreateBatchModal.set(true);
  }

  onIngestSelected() {
    const ids = Array.from(this.selectedIds());
    if (ids.length === 0 || this.isIngesting()) return;

    this.isIngesting.set(true);
    let remaining = ids.length;
    let failures = 0;

    ids.forEach(id => {
      this.batchService.startIngestion(id).subscribe({
        next: () => {
          remaining--;
          if (remaining === 0) this.finishIngestSelected(failures);
        },
        error: () => {
          failures++;
          remaining--;
          if (remaining === 0) this.finishIngestSelected(failures);
        }
      });
    });
  }

  private finishIngestSelected(failures: number) {
    this.isIngesting.set(false);
    this.selectedIds.set(new Set());
    if (failures > 0) {
      this.toast.show('Some batches could not be started.', 'error');
    } else {
      this.toast.show('Ingestion started successfully.', 'success');
    }
    this.loadBatches(this.activeFilter(), this.currentPage());
    this.loadCounts();
  }

  private syncBatchGroups(): void {
    const visible = new Set(this.batches().map(b => b.id.toLowerCase()));
    for (const id of [...this.joinedIds]) {
      if (!visible.has(id)) {
        this.realtime.leaveBatch(id);
        this.joinedIds.delete(id);
      }
    }
    for (const id of visible) {
      if (!this.joinedIds.has(id)) {
        this.joinedIds.add(id);
        void this.realtime.joinBatch(id);
      }
    }
  }

  private registerRealtimeHandlers(): void {
    this.subscriptions.add(
      this.realtime.batchProgress$.subscribe(payload => {
        this.batches.update(items => items.map(b => {
          if (!idsEqual(b.id, payload.batchId)) return b;
          return {
            ...b,
            totalDocuments: payload.total ?? b.totalDocuments,
            processedDocuments: payload.processed ?? b.processedDocuments,
            failedDocuments: payload.failed ?? b.failedDocuments,
            percentComplete: payload.percentComplete ?? b.percentComplete
          };
        }));
      })
    );

    this.subscriptions.add(
      this.realtime.batchStarted$.subscribe(payload => {
        const status = this.normalizeBatchStatus(payload.status);
        this.batches.update(items => items.map(b =>
          idsEqual(b.id, payload.batchId) ? { ...b, status } : b
        ));
      })
    );

    this.subscriptions.add(
      this.realtime.batchCompleted$.subscribe(payload => {
        const status = this.normalizeBatchStatus(payload.status);
        this.batches.update(items => items.map(b =>
          idsEqual(b.id, payload.batchId)
            ? {
                ...b,
                status,
                completedAt: payload.completedAt ?? b.completedAt,
                percentComplete: status === 'Completed' || status === 'Failed' ? 100 : b.percentComplete
              }
            : b
        ));
        this.loadCounts();
        this.loadPendingReviews();
      })
    );

    this.subscriptions.add(
      this.realtime.batchCreationCompleted$.subscribe(payload => {
        const status = this.normalizeBatchStatus(payload.status);
        this.batches.update(items => items.map(b =>
          idsEqual(b.id, payload.batchId)
            ? { ...b, status, totalDocuments: payload.totalDocuments ?? b.totalDocuments }
            : b
        ));
        this.loadCounts();
      })
    );

    this.subscriptions.add(
      this.realtime.batchCreationFailed$.subscribe(payload => {
        this.batches.update(items => items.map(b =>
          idsEqual(b.id, payload.batchId) ? { ...b, status: 'Failed' } : b
        ));
        this.loadCounts();
      })
    );
  }

  private normalizeBatchStatus(status: string | null): string {
    switch ((status ?? '').toLowerCase()) {
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'draft':
      case 'created':
        return 'Draft';
      case 'processing':
        return 'Processing';
      case 'enumerating':
        return 'Preparing';
      default:
        return status ?? 'Processing';
    }
  }
}
